import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /**
   * 保存的滚动位置，用于在关闭导航栏时恢复
   * @type {number | null}
   */
  #savedScrollPosition = null;

  /**
   * 触摸事件处理器，用于阻止背景滚动
   * @type {(event: TouchEvent) => void}
   */
  #preventTouchMove = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    
    // 如果触摸事件发生在导航栏内部，允许滚动
    const menuDrawer = document.querySelector('.menu-drawer');
    if (menuDrawer && menuDrawer.contains(target)) {
      return;
    }
    
    // 如果触摸事件发生在 summary 元素或其子元素上，允许点击（用于打开导航栏）
    const summary = this.refs.details?.querySelector('summary');
    if (summary && (summary === target || summary.contains(target))) {
      return;
    }
    
    // 否则阻止背景滚动
    event.preventDefault();
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event] - The click event
   */
  toggle(event) {
    // 如果事件来自 summary，阻止默认的 details toggle 行为
    // 因为我们手动控制 open 状态
    if (event && event.target instanceof Element) {
      const summary = event.target.closest('summary');
      if (summary && summary === this.refs.details?.querySelector('summary')) {
        event.preventDefault();
      }
    }
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // 保存当前滚动位置
    this.#savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // 设置open属性以触发scroll-lock机制
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      // 锁定背景页面滚动
      this.#lockBodyScroll();
    });

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    // 移除open属性以触发scroll-lock机制
    details.removeAttribute('open');

    // 解锁背景页面滚动
    this.#unlockBodyScroll();

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * 锁定背景页面滚动
   */
  #lockBodyScroll() {
    const html = document.documentElement;
    const body = document.body;

    // 保存当前滚动位置
    if (this.#savedScrollPosition === null) {
      this.#savedScrollPosition = window.pageYOffset || html.scrollTop;
    }

    // 设置固定定位并保持滚动位置
    const scrollY = this.#savedScrollPosition;
    html.style.position = 'fixed';
    html.style.top = `-${scrollY}px`;
    html.style.width = '100%';
    html.style.overflow = 'hidden';

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    // 阻止触摸滚动事件（移动端）
    // 注意：只阻止 touchmove，不阻止 touchstart，因为 touchstart 会影响点击事件
    document.addEventListener('touchmove', this.#preventTouchMove, { passive: false });
  }

  /**
   * 解锁背景页面滚动
   */
  #unlockBodyScroll() {
    const html = document.documentElement;
    const body = document.body;

    // 恢复样式
    html.style.position = '';
    html.style.top = '';
    html.style.width = '';
    html.style.overflow = '';

    body.style.position = '';
    body.style.top = '';
    body.style.width = '';
    body.style.overflow = '';

    // 恢复滚动位置
    if (this.#savedScrollPosition !== null) {
      window.scrollTo(0, this.#savedScrollPosition);
      this.#savedScrollPosition = null;
    }

    // 移除触摸事件监听器
    document.removeEventListener('touchmove', this.#preventTouchMove);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
